/**
 * Fetches and enriches biomarker attachment data for posts.
 * Used by PostFeed and PostCard so shared logic lives here (avoids circular deps).
 */
import { supabase } from '../supabase';
import { getBiomarkerSelectFields } from './biomarkerUtils';
import { dedupeRequest, getCacheKey } from './queryDeduplication';
import { sanitizeErrorForLog } from './sanitizeErrorForLog';

export const fetchBiomarkerAttachments = async (postIds, posts = []) => {
  if (!postIds.length) return {};

  const cacheKey = getCacheKey('fetchBiomarkerAttachments', postIds);

  return dedupeRequest(cacheKey, async () => {
    try {
      const postAuthorMap = new Map();
      if (posts && posts.length > 0) {
        posts.forEach((post) => {
          if (post.id && post.user_id) {
            postAuthorMap.set(post.id, post.user_id);
          }
        });
      }

      const { data: attachmentsData, error: attError } = await supabase
        .from('post_attachments')
        .select(`
          id, post_id, type, sort_order,
          post_attachment_biomarkers:post_attachment_biomarkers_post_attachment_id_fkey(
            biomarker_id,
            biomarker_result_id
          )
        `)
        .in('post_id', postIds)
        .eq('type', 'biomarker');

      if (attError) {
        const is406Error = attError.status === 406 || attError.message?.includes('406') || attError.code === '406';
        if (is406Error) {
          console.error('🚨 406 ERROR in fetchBiomarkerAttachments (attachments query):', {
            error: attError,
            postIds,
            timestamp: new Date().toISOString()
          });
        }
        return {};
      }
      if (!attachmentsData || attachmentsData.length === 0) return {};

      const biomarkerIds = new Set();
      const attachmentInfoMap = new Map();
      const lockedResultIds = new Set();

      attachmentsData.forEach((att) => {
        if (att.post_attachment_biomarkers && att.post_attachment_biomarkers.length > 0) {
          att.post_attachment_biomarkers.forEach((link) => {
            if (link.biomarker_id) {
              biomarkerIds.add(link.biomarker_id);
              attachmentInfoMap.set(att.id, {
                biomarker_id: link.biomarker_id,
                biomarker_result_id: link.biomarker_result_id || null
              });
              if (link.biomarker_result_id) lockedResultIds.add(link.biomarker_result_id);
            }
          });
        }
      });

      if (biomarkerIds.size === 0) return {};

      const biomarkerIdsArray = Array.from(biomarkerIds);
      const lockedResultIdsArray = Array.from(lockedResultIds);

      const [biomarkersResponse, lockedResultsResponse, biomarkerResultsResponse] = await Promise.all([
        supabase.from('biomarkers').select(getBiomarkerSelectFields()).in('id', biomarkerIdsArray),
        lockedResultIdsArray.length > 0
          ? supabase
              .from('biomarker_results')
              .select('id, biomarker_id, value, unit_override, panel_id, created_at')
              .in('id', lockedResultIdsArray)
          : Promise.resolve({ data: [], error: null }),
        supabase
          .from('biomarker_results')
          .select('id, biomarker_id, value, unit_override, panel_id, created_at')
          .in('biomarker_id', biomarkerIdsArray)
          .order('created_at', { ascending: false })
          .limit(1000)
      ]);

      if (biomarkersResponse.error || !biomarkersResponse.data) return {};
      if (lockedResultsResponse?.error) {
        console.error('Error fetching locked biomarker results:', sanitizeErrorForLog(lockedResultsResponse.error));
      }
      if (biomarkerResultsResponse.error) {
        console.error('Error fetching biomarker results:', sanitizeErrorForLog(biomarkerResultsResponse.error));
        return {};
      }

      const biomarkers = biomarkersResponse.data;
      const lockedResults = lockedResultsResponse?.data || [];
      const biomarkerResults = biomarkerResultsResponse.data || [];

      const panelIds = [...new Set([...lockedResults, ...biomarkerResults].map((r) => r.panel_id).filter(Boolean))];
      let panels = null;
      if (panelIds.length > 0) {
        const { data: panelsData, error: panelsError } = await supabase
          .from('biomarker_panel_results')
          .select('id, user_id, test_date')
          .in('id', panelIds);
        if (!panelsError && panelsData) {
          panels = panelsData;
        }
      }

      const panelMap = panels ? new Map(panels.map((p) => [p.id, p])) : new Map();

      let filteredResults = biomarkerResults;
      if (biomarkerResults.length > 0 && panels && postAuthorMap.size > 0) {
        const panelUserMap = new Map(panels.map((p) => [p.id, p.user_id]));
        const postAuthorIds = new Set(postAuthorMap.values());
        filteredResults = biomarkerResults.filter((result) => {
          const panelUserId = panelUserMap.get(result.panel_id);
          return panelUserId && postAuthorIds.has(panelUserId);
        });
      }

      const lockedResultMap = new Map();
      lockedResults.forEach((r) => {
        lockedResultMap.set(r.id, { ...r, lab_panels: panelMap.get(r.panel_id) });
      });

      const biomarkerMap = {};
      biomarkers.forEach((bm) => {
        const results = filteredResults
          .filter((lr) => lr.biomarker_id === bm.id)
          .slice(0, 10);
        biomarkerMap[bm.id] = {
          ...bm,
          lab_results: results.map((r) => ({ ...r, lab_panels: panelMap.get(r.panel_id) }))
        };
      });

      const result = {};
      attachmentsData.forEach((att) => {
        const info = attachmentInfoMap.get(att.id);
        if (!info || !biomarkerMap[info.biomarker_id]) return;
        const bmMeta = biomarkerMap[info.biomarker_id];
        let biomarkerForAttachment;
        if (info.biomarker_result_id) {
          const locked = lockedResultMap.get(info.biomarker_result_id);
          if (locked) {
            biomarkerForAttachment = { ...bmMeta, lab_results: [locked] };
          } else {
            biomarkerForAttachment = { ...bmMeta, lab_results: [], resultDeleted: true };
          }
        } else {
          biomarkerForAttachment = { ...bmMeta, lab_results: [], resultDeleted: true };
        }
        if (!result[att.post_id]) result[att.post_id] = {};
        result[att.post_id][att.id] = {
          attachment: { id: att.id, post_id: att.post_id, type: att.type, sort_order: att.sort_order },
          biomarkers: [biomarkerForAttachment]
        };
      });

      return result;
    } catch (err) {
      console.error('Error fetching biomarker attachments:', err);
      return {};
    }
  });
};
